import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, CheckCircle, Download, PlayCircle, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = window.location.origin;

export default function VeilleProPage() {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [user, setUser] = useState(null);
  const [userPurchases, setUserPurchases] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      const u = JSON.parse(stored);
      setUser(u);
      // Fetch purchases
      axios.get(`${BACKEND_URL}/api/payments/user/${encodeURIComponent(u.email)}`)
        .then(res => setUserPurchases(res.data))
        .catch(() => {});
    }
    // Fetch veille-pro program
    axios.get(`${BACKEND_URL}/api/programs`)
      .then(res => {
        const vp = res.data.find(p => p.id === 'veille-pro');
        setProgram(vp);
      })
      .catch(() => toast.error('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  const hasPurchased = (formationId) => {
    return userPurchases.some(p => p.programId === 'veille-pro' && p.kitId === formationId && p.status === 'completed');
  };

  const handleBuy = async (formation) => {
    if (!user) {
      toast.error('Veuillez vous connecter pour acheter');
      navigate('/login');
      return;
    }
    setPurchasing(formation.id);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/payments/checkout`, {
        programId: 'veille-pro',
        formula: 'kit',
        kitId: formation.id,
        kitTitle: formation.title,
        price: formation.price,
        email: user.email,
        origin: window.location.origin
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error('Erreur lors de la création du paiement');
    } finally {
      setPurchasing(null);
    }
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <div className="spinner" />
    </div>
  );

  if (!program) return (
    <div style={{ textAlign: 'center', padding: '4rem 2rem' }}>
      <h1 className="heading-1">Formations bientôt disponibles</h1>
      <p className="body-text" style={{ color: 'var(--text-muted)' }}>Les formations de veille professionnelle arrivent prochainement.</p>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB', paddingTop: '70px' }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        color: 'white',
        padding: '4rem 2rem',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ background: 'rgba(255,255,255,0.1)', display: 'inline-block', padding: '0.4rem 1.2rem', borderRadius: '2rem', fontSize: '0.85rem', fontWeight: '600', marginBottom: '1.5rem', letterSpacing: '0.03em' }}>
            VEILLE PROFESSIONNELLE
          </div>
          <h1 style={{ fontSize: 'clamp(1.5rem, 5vw, 2.5rem)', fontWeight: 'bold', marginBottom: '1rem', lineHeight: 1.2 }} data-testid="veille-pro-title">
            {program.title}
          </h1>
          <p style={{ fontSize: '1.1rem', opacity: 0.85, maxWidth: '600px', margin: '0 auto' }}>
            {program.description}
          </p>
        </div>
      </div>

      {/* Formations grid */}
      <div style={{ maxWidth: '1100px', margin: '-2rem auto 0', padding: '0 2rem 4rem', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: '2rem' }}>
          {program.formations?.map((formation) => {
            const owned = hasPurchased(formation.id);
            return (
              <div key={formation.id} className="card hover-lift" style={{
                overflow: 'hidden',
                border: owned ? '2px solid #10B981' : '1px solid #E5E7EB',
                position: 'relative'
              }} data-testid={`formation-card-${formation.id}`}>
                {owned && (
                  <div style={{ position: 'absolute', top: '1rem', right: '1rem', background: '#10B981', color: 'white', padding: '0.3rem 0.8rem', borderRadius: '2rem', fontSize: '0.75rem', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '0.3rem', zIndex: 2 }}>
                    <CheckCircle size={14} /> Acheté
                  </div>
                )}

                {/* Header */}
                <div style={{
                  background: `linear-gradient(135deg, ${formation.color} 0%, ${formation.color}CC 100%)`,
                  padding: '2rem',
                  color: 'white'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                    <BookOpen size={24} />
                    <span style={{ fontSize: '0.8rem', opacity: 0.85 }}>{formation.slideCount} modules interactifs</span>
                  </div>
                  <h2 style={{ fontSize: '1.3rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>{formation.title}</h2>
                  <p style={{ fontSize: '0.9rem', opacity: 0.85 }}>{formation.subtitle}</p>
                </div>

                {/* Body */}
                <div style={{ padding: '1.5rem' }}>
                  <p style={{ color: '#6B7280', fontSize: '0.9rem', lineHeight: 1.6, marginBottom: '1.25rem' }}>
                    {formation.description}
                  </p>

                  {/* Includes */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    {formation.includes?.map((item, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: '#374151' }}>
                        <CheckCircle size={14} color="#10B981" />
                        {item}
                      </div>
                    ))}
                  </div>

                  {/* Price & CTA */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <span style={{ fontSize: '2rem', fontWeight: '800', color: '#1F2937' }}>{formation.price}€</span>
                    </div>
                    {owned ? (
                      <button
                        className="btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        onClick={() => navigate('/dashboard?tab=veille-pro')}
                        data-testid={`access-formation-${formation.id}`}
                      >
                        <PlayCircle size={18} /> Accéder à la formation
                      </button>
                    ) : (
                      <button
                        className="btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        disabled={purchasing === formation.id}
                        onClick={() => handleBuy(formation)}
                        data-testid={`buy-formation-${formation.id}`}
                      >
                        <ShoppingBag size={18} />
                        {purchasing === formation.id ? 'Redirection...' : 'Acheter la formation'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Coming soon note */}
        <div style={{ textAlign: 'center', marginTop: '3rem', padding: '2rem', background: 'white', borderRadius: '1rem', border: '1px dashed #D1D5DB' }}>
          <p style={{ color: '#6B7280', fontSize: '0.95rem' }}>
            D'autres formations arrivent prochainement ! Restez connecté(e) pour enrichir votre pratique professionnelle.
          </p>
        </div>
      </div>
    </div>
  );
}
